import { Injectable, NgZone } from '@angular/core'
import { Observable, BehaviorSubject, of } from 'rxjs'
import {
    catchError,
    debounceTime,
    finalize,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs/operators'
import {
    MessageItem,
    FilterCriteria,
    PatientDetails,
    ApiPatientListResponse,
    ApiPatientListItem,
    ApiConversationResponse,
    ApiConversationItem,
    PatientDetailsResponse,
    SearchParams,
    ApiResponse,
    PatientTaskOpen,
    AgentListResponse,
} from '../models/user.model'
import { HttpClient, HttpParams } from '@angular/common/http'
import { ToastrService } from 'ngx-toastr'
import * as moment from 'moment'

@Injectable({ providedIn: 'root' })
export class MessageUserService {
    public _selectedPatientSubject = new BehaviorSubject<PatientDetails | null>(
        null,
    )
    readonly selectedPatient$ = this._selectedPatientSubject.asObservable()

    public selectedPatientId: string | null = null

    private _allFetchedPatients: MessageItem[] = []

    public _patientsSubject = new BehaviorSubject<MessageItem[] | null>(null)
    readonly patients$ = this._patientsSubject.asObservable()

    public _communicationTabSubject = new BehaviorSubject<string>('SMS')
    readonly communicationTab$ = this._communicationTabSubject.asObservable()

    private _totalPatientCount: number = 0
    public _selectedChatSubject = new BehaviorSubject<ApiConversationItem[]>([])
    readonly selectedChat$ = this._selectedChatSubject.asObservable()
    private _totalMessageCount: number = 0

    private _loadingMoreSubject = new BehaviorSubject<boolean>(false)
    readonly loadingMore$ = this._loadingMoreSubject.asObservable()

    private _hasMorePagesSubject = new BehaviorSubject<boolean>(true)
    readonly hasMorePages$ = this._hasMorePagesSubject.asObservable()

    private _loadingNewPatient = new BehaviorSubject<boolean>(true)
    readonly loadingNewPatient$ = this._loadingNewPatient.asObservable()

    public filterCriteria: FilterCriteria = {
        selectedUserIds: [],
        communicationType: 'All',
        dateRangePreset: null,
        customDateRange: null,
        searchTerm: null,
    }
    private currentFilterStatus: string | null = 'All'

    private _isLoadingMessages = new BehaviorSubject<boolean>(false)
    readonly isLoadingMessages$ = this._isLoadingMessages.asObservable()

    private _loadingMoreMessages = new BehaviorSubject<boolean>(false)
    readonly loadingMoreMessages$ = this._loadingMoreMessages.asObservable()

    private _hasMoreMessagesSubject = new BehaviorSubject<boolean>(true)
    readonly hasMoreMessages$ = this._hasMoreMessagesSubject.asObservable()

    private _isLoadingFeedSubject = new BehaviorSubject<boolean>(false)
    readonly isLoadingChatfeed$ = this._isLoadingFeedSubject.asObservable()

    public _totalUnreadCount = new BehaviorSubject<number>(0)
    readonly totalUnreadCount$ = this._totalUnreadCount.asObservable()

    public _totalSMSUnreadCount = new BehaviorSubject<number>(0)
    readonly totalSMSUnreadCount$ = this._totalSMSUnreadCount.asObservable()

    public _totalEMAILUnreadCount = new BehaviorSubject<number>(0)
    readonly totalEMAILUnreadCount$ = this._totalEMAILUnreadCount.asObservable()

    public _leaveChatWebsocket = new BehaviorSubject<boolean>(false)
    readonly leaveChatWebsocket$ = this._leaveChatWebsocket.asObservable()


    public _clearFilterEvent = new BehaviorSubject<boolean>(false)
    readonly clearFilterEvent$ = this._clearFilterEvent.asObservable()


    public _UpdateFollowButton = new BehaviorSubject<boolean>(false)
    readonly updateFollowButton$ = this._UpdateFollowButton.asObservable()

    public _ZeroFollowerCount= new BehaviorSubject<number>(0)
    readonly zeroFollowerCount$ = this._ZeroFollowerCount.asObservable()


    public leaveRoomEvent = new BehaviorSubject<boolean>(false) 
    readonly leaveRoomEvent$ = this.leaveRoomEvent.asObservable()

    public autoScrollToBottomEvent = new BehaviorSubject<boolean>(false)
    readonly autoScrollToBottomEvent$ = this.autoScrollToBottomEvent.asObservable()
   
    

    enabelZeroFollowerFilter: boolean = false;

    private readonly typeMap: Record<string, number> = {
        SMS: 1,
        EMAIL: 2,
        PHONE: 3,
    }


    private nextCursor: string | null = null
    private cursorHasUnread: number | null = null
    private cursorHasContent: number | null = null
    private cursorPatientCreatedAt: string | null = null
    private cursorPatientId: string | null = null
    private direction: string = 'next'
    private morePageSize = 16
    private isLoadingMore = false
    private nextMessageCursor: string | null = null

    private _selectMessageSubject = new BehaviorSubject<string | null>(null)

    constructor(
        private http: HttpClient,
        private zone: NgZone,
        private toastService: ToastrService,
    ) {
        this._selectMessageSubject
            .pipe(
                debounceTime(300),
                switchMap((id) => {
                    if (!id || id === this.selectedPatientId) {
                        this.zone.run(() => {
                            this._isLoadingFeedSubject.next(false)
                        })
                        return of(void 0)
                    }
                    this.zone.run(() => {
                        this._isLoadingFeedSubject.next(true)
                        this.selectedPatientId = id
                        this._communicationTabSubject.next(
                            this.filterCriteria.communicationType &&
                                this.filterCriteria.communicationType !== 'All'
                                ? this.filterCriteria.communicationType.toUpperCase()
                                : 'SMS',
                        )
                    })

                    // Subscribe to getChatAndSelectedPateint
                    return this.getChatAndSelectedPateint(id).pipe(
                        map(() => void 0),
                        catchError((err) => {
                            console.error('Error in getChatAndSelectedPateint')
                            return of(void 0)
                        }),
                    )
                }),
                finalize(() => {
                    this.zone.run(() => {
                        this._isLoadingFeedSubject.next(false)
                    })
                }),
                catchError((err) => {
                    console.error('Error in selectMessage pipeline', err)
                    this.zone.run(() => {
                        this._isLoadingFeedSubject.next(false)
                    })
                    return of(void 0)
                }),
            )
            .subscribe()
    }
    selectMessage(id: string): Observable<void> {
        this._selectMessageSubject.next(id)
        return of(void 0)
    }

    loadInitialMessages(
        patientId: string,
        limit: number = 20,
    ): Observable<ApiConversationItem[]> {
        this._isLoadingMessages.next(true)
        this.nextMessageCursor = null
        this._totalMessageCount = 0
        this._hasMoreMessagesSubject.next(true)
        return this.fetchMessages(
            patientId,
            limit,
            this.nextMessageCursor,
            this.direction,
        ).pipe(
            tap(
                ({
                    messages,
                    totalCount,
                    totalUnreadSMS,
                    totalUnreadEmail,
                    nextCursor,
                }) => {
                    this.zone.run(() => {
                        this._totalMessageCount = totalCount
                        this._totalSMSUnreadCount.next(totalUnreadSMS)
                        this._totalEMAILUnreadCount.next(totalUnreadEmail)
                        this.nextMessageCursor = nextCursor
                        if (messages.length >= totalCount) {
                            this._hasMoreMessagesSubject.next(false)
                        } else {
                            this._hasMoreMessagesSubject.next(true)
                        }
                        this._selectedChatSubject.next(messages.reverse())
                    })
                    // Mark messages as read and update counts
                    this.markCurrentChatAsRead(patientId).subscribe(() => {
                        const communicationType =
                            this._communicationTabSubject
                                .getValue()
                                .toUpperCase() || 'SMS'
                        const currentPatients =
                            this._patientsSubject.value || []
                        const patient = currentPatients.find(
                            (p) => p.id === patientId,
                        )

                        if (patient) {
                            // Store previous message count for total unread count adjustment
                            const previousMessageCount =
                                patient.messageCount || 0

                            // Update unread counts based on communication type
                            let newSMSUnreadCount =
                                this._totalSMSUnreadCount.value
                            let newEmailUnreadCount =
                                this._totalEMAILUnreadCount.value

                            if (communicationType === 'SMS') {
                                newSMSUnreadCount = 0 // All SMS messages marked as read
                            } else if (communicationType === 'EMAIL') {
                                newEmailUnreadCount = 0 // All email messages marked as read
                            }

                            // Update subjects with new unread counts
                            this._totalSMSUnreadCount.next(newSMSUnreadCount)
                            this._totalEMAILUnreadCount.next(
                                newEmailUnreadCount,
                            )

                            // Update patient’s messageCount (sum of SMS and email unread counts)
                            const newMessageCount =
                                newSMSUnreadCount + newEmailUnreadCount
                            const updatedPatients = currentPatients.map((p) =>
                                p.id === patientId
                                    ? { ...p, messageCount: newMessageCount }
                                    : p,
                            )
                            this._patientsSubject.next([...updatedPatients])

                            // Update total unread count based on change in messageCount
                            const hadUnreadMessages = previousMessageCount > 0
                            const hasUnreadMessages = newMessageCount > 0
                            if (hadUnreadMessages && !hasUnreadMessages) {
                                const previousUnreadCount =
                                    this._totalUnreadCount.getValue()
                                this._totalUnreadCount.next(
                                    previousUnreadCount - 1,
                                )
                            }
                        }
                    })
                },
            ),
            map(({ messages }) => messages),
            finalize(() => {
                this.zone.run(() => {
                    this._isLoadingMessages.next(false)
                })
            }),
            catchError((err) => {
                console.error(`Error loading messages for patient ID`)
                return of([])
            }),
        )
    }

    loadMoreMessages(
        patientId: string,
        limit: number = 20,
    ): Observable<ApiConversationItem[]> {
        if (!this._hasMoreMessagesSubject.value || !this.nextMessageCursor) {
            return of([])
        }
        this._loadingMoreMessages.next(true)
        return this.fetchMessages(
            patientId,
            limit,
            this.nextMessageCursor,
            this.direction,
        ).pipe(
            tap(({ messages, totalCount, nextCursor }) => {
                this.zone.run(() => {
                    this._totalMessageCount = totalCount
                    this.nextMessageCursor = nextCursor
                    const currentChat = this._selectedChatSubject.value
                    this._selectedChatSubject.next([
                        ...messages.reverse(),
                        ...currentChat,
                    ])
                    if (this._selectedChatSubject.value.length >= totalCount) {
                        this._hasMoreMessagesSubject.next(false)
                    } else {
                        this._hasMoreMessagesSubject.next(true)
                    }
                })
            }),
            map(({ messages }) => messages),
            finalize(() => {
                this.zone.run(() => {
                    this._loadingMoreMessages.next(false)
                })
            }),
            catchError((err) => {
                console.error(`Error loading more messages for patient ID`)
                return of([])
            }),
        )
    }

    private fetchMessages(
        patientId: string,
        limit: number,
        cursor: string | null,
        direction: string,
    ): Observable<{
        messages: ApiConversationItem[]
        totalCount: number
        totalUnreadSMS: number
        totalUnreadEmail: number
        nextCursor: string | null
    }> {
        const communicationType = this._communicationTabSubject
            .getValue()
            .toUpperCase()
        let params = new HttpParams()
            .set('limit', String(limit))
            .set('direction', direction)
        if (cursor) {
            params = params.set('cursor', cursor)
        }
        return this.http
            .get<ApiConversationResponse>(
                `/api/v2/platform/inbox-messaging/patient-conversation-history/${patientId}/${communicationType}`,
                { params },
            )
            .pipe(
                map((response) => ({
                    messages: response.data.map((apiMessage) =>
                        this.mapApiMessageToChatMessage(apiMessage),
                    ),
                    totalCount: response.meta.totalCount,
                    totalUnreadSMS: response.meta.totalUnreadSMS,
                    totalUnreadEmail: response.meta.totalUnreadEmail,
                    nextCursor: response.meta.nextCursor || null,
                })),
                catchError((err) => {
                    console.error('Error fetching messages')
                    return of({
                        messages: [],
                        totalCount: 0,
                        totalUnreadSMS: 0,
                        totalUnreadEmail: 0,
                        nextCursor: null,
                    })
                }),
            )
    }


    refreshPatientList(): Observable<MessageItem[]> {
        return this.loadInitialPatients(false);
    }

    loadInitialPatients(updateChat: boolean = true): Observable<MessageItem[]> {
        this._loadingNewPatient.next(true)
        this.nextCursor = null
        this.cursorPatientId = null
        this.cursorHasUnread = null
        this.cursorHasContent = null
        this.cursorPatientCreatedAt = null
        this._totalPatientCount = 0
        this._allFetchedPatients = []
        this._patientsSubject.next([])
        return this.fetchPage(
            this.morePageSize,
            this.nextCursor,
            this.cursorPatientId,
            this.direction,
        ).pipe(
            tap(({ items, totalCount, nextCursor, nextCursorPatientId, cursorHasUnread , cursorHasContent, cursorPatientCreatedAt }) => {
                this._totalPatientCount = totalCount
                this.nextCursor = nextCursor
                this.cursorPatientId = nextCursorPatientId
                this.cursorHasUnread = cursorHasUnread
                this.cursorHasContent = cursorHasContent
                this.cursorPatientCreatedAt = cursorPatientCreatedAt
                this._allFetchedPatients = [...items]
                if (this._allFetchedPatients.length >= totalCount) {
                    this._hasMorePagesSubject.next(false)
                } else {
                    this._hasMorePagesSubject.next(true)
                }
                this.markSelectionFlag(this._allFetchedPatients)
                let newlist = this.markAsReadWhenFirstload(
                    this._allFetchedPatients,
                )
                this._patientsSubject.next([...newlist])
                this._loadingNewPatient.next(false)
                if (updateChat) {
                    this.getChatAndSelectedPateint(
                        this.selectedPatientId,
                    ).subscribe({
                        error: (err) => {
                            console.error(
                                'Error in getChatAndSelectedPateint from loadInitialPatients',
                            )
                            this._isLoadingFeedSubject.next(false)
                        },
                        complete: () => {},
                    })
                }
            }),
            map(({ items }) => items),
            catchError((err) => {
                console.error('Error in loadInitialPatients', err)
                return of([])
            }),
        )
    }

    loadMorePatients(): Observable<MessageItem[]> {
        if (
            this.isLoadingMore ||
            this._allFetchedPatients.length >= this._totalPatientCount
        ) {
            this._loadingMoreSubject.next(false)
            return of([])
        }
        this.isLoadingMore = true
        this._loadingMoreSubject.next(true)
        return this.fetchPage(
            this.morePageSize,
            this.nextCursor,
            this.cursorPatientId,
            this.direction,
        ).pipe(
            tap(({ items, totalCount, nextCursor, nextCursorPatientId, cursorHasUnread, cursorHasContent, cursorPatientCreatedAt }) => {
                this.zone.run(() => {
                    this._totalPatientCount = totalCount
                    this.nextCursor = nextCursor
                    this.cursorPatientId = nextCursorPatientId
                    this.cursorHasUnread = cursorHasUnread
                    this.cursorPatientCreatedAt = cursorPatientCreatedAt
                    this.cursorHasContent = cursorHasContent
                    this._allFetchedPatients.push(...items)
                    if (this._allFetchedPatients.length >= totalCount) {
                        this._hasMorePagesSubject.next(false)
                    } else {
                        this._hasMorePagesSubject.next(true)
                    }
                    this.markSelectionFlag(this._allFetchedPatients)
                    this._patientsSubject.next([...this._patientsSubject.value, ...items])

                })
            }),
            map(({ items }) => items),
            catchError((err) => {
                console.error(
                    'Error fetching next page of patient conversations',
                )
                this.zone.run(() => {
                    this._hasMorePagesSubject.next(false) // Stop further loading on error
                    this._patientsSubject.next([...this._patientsSubject.value]) // Ensure current items are shown
                })
                return of([])
            }),
            finalize(() => {
                this.zone.run(() => {
                    this.isLoadingMore = false
                    this._loadingMoreSubject.next(false)
                })
            }),
        )
    }

    private fetchPage(
        limit: number,
        cursor: string | null,
        cursorPatientId: string | null,
        direction: string,
    ): Observable<{
        items: MessageItem[]
        totalCount: number
        nextCursor: string | null
        nextCursorPatientId: string | null
        cursorHasUnread: number 
        cursorHasContent: number 
        cursorPatientCreatedAt: string 
    }> {
        const zeroFollowerUrl = '/api/v2/platform/inbox-messaging/no-followers-patient-conversations'
        const normalUrl = '/api/v2/platform/inbox-messaging/patient-conversations'
        const paramsObj: Record<string, any> = {
            limit: String(limit),
            direction: direction,
            search:
                this.filterCriteria.searchTerm &&
                this.filterCriteria.searchTerm.trim() !== ''
                    ? this.filterCriteria.searchTerm.trim()
                    : '',
            fromDate: this.filterCriteria.customDateRange?.start
                ? moment(this.filterCriteria.customDateRange?.start).format(
                      'YYYY-MM-DD',
                  )
                : null,
            toDate: this.filterCriteria.customDateRange?.end
                ? moment(this.filterCriteria.customDateRange?.end).format(
                      'YYYY-MM-DD',
                  )
                : null,
            type:
                this.filterCriteria.communicationType == 'All'
                    ? null
                    : this.filterCriteria.communicationType.toUpperCase(),
            agentIds:
                this.filterCriteria.selectedUserIds.length > 0
                    ? this.filterCriteria.selectedUserIds.join(',')
                    : undefined,
            isSelectAll: this.filterCriteria.isSelectAll || false,
        }
    
        paramsObj.cursorHasContent = this.cursorHasContent
        paramsObj.cursorPatientCreatedAt = this.cursorPatientCreatedAt
        if(cursor === null){
            paramsObj.cursor = ''
        
        }else{
            paramsObj.cursor = cursor
        }
        if (this.cursorHasUnread === 0 || this.cursorHasUnread === 1) {
            paramsObj.cursorHasUnread = this.cursorHasUnread
        }
        if(cursorPatientId === null){
            paramsObj.cursorPatientId = ''
        }else{
            paramsObj.cursorPatientId = cursorPatientId
        }
        if (
            this.currentFilterStatus &&
            this.currentFilterStatus.trim() === 'All'
        ) {
            paramsObj.unreadOnly = false
        } else {
            paramsObj.unreadOnly = true
            this._leaveChatWebsocket.next(true)
            this.clearChat()
        }
        Object.keys(paramsObj).forEach(
            (key) => paramsObj[key] == null && delete paramsObj[key],
        )
        return this.http
            .get<ApiPatientListResponse>(
                this.enabelZeroFollowerFilter ? zeroFollowerUrl : normalUrl,
                { params: paramsObj },
            )
            .pipe(
                map((resp) => {
                    this.zone.run(() => {
                        this._totalUnreadCount.next(
                            resp.meta.totalUnreadMessages || 0,
                        )
                    })
                    const items: MessageItem[] = resp.data.map((apiItem) =>
                        this.mapApiToMessageItem(apiItem),
                    )
                    return {
                        items,
                        totalCount: resp.meta.totalCount,
                        nextCursor: resp.meta.nextCursor?.createdAt,
                        nextCursorPatientId:
                            resp.meta.nextCursor?.patientId,
                        cursorHasUnread: resp.meta.nextCursor?.cursorHasUnread,
                        cursorHasContent: resp.meta.nextCursor?.cursorHasContent,
                        cursorPatientCreatedAt: resp.meta.nextCursor?.cursorPatientCreatedAt,
                    }
                }),
                catchError((err) => {
                    console.error('Error fetching patient conversations')
                    return of({
                        items: [],
                        totalCount: 0,
                        nextCursor: null,
                        nextCursorPatientId: null,
                        cursorHasUnread: 0,
                        cursorHasContent: 0,
                        cursorPatientCreatedAt: '',
                    })
                }),
            )
    }

    private markSelectionFlag(list: MessageItem[]): void {
        if (this.selectedPatientId) {
            list.forEach((item) => {
                item.isSelected = item.id === this.selectedPatientId
            })
        }
    }

    private markAsReadWhenFirstload(list: MessageItem[]): MessageItem[] {
        return list.map((item) => {
            if (item.id === this.selectedPatientId) {
                this.markCurrentChatAsRead(this.selectedPatientId).subscribe()
            }
            return item
        })
    }

    public mapApiToMessageItem(api: ApiPatientListItem): MessageItem {
        const first = (api.firstName || '').trim()
        const middle = (api.middleName || '').trim()
        const last = (api.lastName || '').trim()
        const patientName = [first, middle, last].filter(Boolean).join(' ')
        const initials =
            `${first.charAt(0) || ''}${middle.charAt(0) || ''}${last.charAt(0) || ''}`.toUpperCase()
        let unreadCount = 0
        if (typeof api.unreadMessages === 'number') {
            unreadCount = api.unreadMessages || 0
        } else if (
            api.unreadMessages &&
            typeof api.unreadMessages === 'object'
        ) {
            unreadCount = api.unreadMessages.totalUnreadMessages || 0
        }

        return {
            id: api.patientId,
            patientInitials: initials,
            patientName,
            lastMessage: api.content,
            lastMessageDate: api.createdAt,
            messageCount: unreadCount,
            profilePictureUrl: api.patientProfileUrl || '',
            hasMMSAttachment: api.hasMMSAttachment,
            isSecured: false,
            isSelected: false,
        }
    }

    applyFilterCriteria(
        criteria: FilterCriteria,
    ): Observable<{ messages: MessageItem[] }> {
        this.enabelZeroFollowerFilter = criteria.enableZeroFollowerFilter;
        // Store previous filter criteria for comparison
        const previousCriteria = { ...this.filterCriteria }

        // Update filter criteria with new values or defaults
        this.filterCriteria = criteria || {
            selectedUserIds: [],
            communicationType: 'All',
            dateRangePreset: null,
            customDateRange: null,
            searchTerm: null,
        }

        // Check if only the tag (Unread/All) has changed
        const onlyTagChanged =
            previousCriteria.selectedUserIds.join() ===
                this.filterCriteria.selectedUserIds.join() &&
            previousCriteria.communicationType ===
                this.filterCriteria.communicationType &&
            previousCriteria.dateRangePreset ===
                this.filterCriteria.dateRangePreset &&
            previousCriteria.customDateRange?.start?.getTime() ===
                this.filterCriteria.customDateRange?.start?.getTime() &&
            previousCriteria.customDateRange?.end?.getTime() ===
                this.filterCriteria.customDateRange?.end?.getTime() &&
            previousCriteria.searchTerm === this.filterCriteria.searchTerm &&
            previousCriteria.enableZeroFollowerFilter === this.filterCriteria.enableZeroFollowerFilter &&
            previousCriteria.tag !== this.filterCriteria.tag

        // Update communication tab if communicationType is not 'All'
        if (
            this.filterCriteria.communicationType &&
            this.filterCriteria.communicationType !== 'All'
        ) {
            this._communicationTabSubject.next(
                this.filterCriteria.communicationType.toUpperCase(),
            )
            // Only clear state if not just switching Unread/All tabs
            if (!onlyTagChanged) {
                this.clearStateForFilter()
            }
        } else if (!onlyTagChanged) {
            // If communicationType is 'All' and other filters changed, clear state
            this._communicationTabSubject.next('SMS') // Default to SMS
            this.clearStateForFilter()
        }

        // Update current filter status
        this.currentFilterStatus = this.filterCriteria.tag || 'All'

        return this.loadInitialPatients().pipe(
            map((items) => ({ messages: items })),
        )
    }
    changeCommunicationType(
        id: string,
        tab: string,
    ): Observable<ApiConversationItem[]> {
        this._communicationTabSubject.next(tab.toUpperCase())

        return this.markCurrentChatAsRead(id).pipe(
            tap(() => {
                this.zone.run(() => {
                    if (this._communicationTabSubject.value === 'SMS') {
                        this._totalSMSUnreadCount.next(0)
                    }
                    if (this._communicationTabSubject.value === 'EMAIL') {
                        this._totalEMAILUnreadCount.next(0)
                    }
                })
            }),
            switchMap(() => this.loadInitialMessages(id)),
            catchError((err) => {
                console.error(`Error in changeCommunicationType for patient ID`)
                return of([])
            }),
        )
    }

    getSelectedPatientDetails(patientId: string): Observable<PatientDetails> {
        return this.http
            .get<PatientDetailsResponse>(
                `/api/v2/platform/inbox-messaging/patient-detail/${patientId}`,
            )
            .pipe(
                map((response: PatientDetailsResponse) => response.data || {}),
                catchError((err) => {
                    return of({})
                }),
            )
    }

    public mapApiMessageToChatMessage(
        apiMessage: ApiConversationItem,
    ): ApiConversationItem {
        const communicationMode =
            this._communicationTabSubject.getValue().toUpperCase() || 'SMS'
        const agentInitials = (
            (apiMessage.agentName?.charAt(0) || '') +
            (apiMessage.agentName?.charAt(1) || '')
        ).toUpperCase()
        const patientInitials = (
            (apiMessage.patientName?.charAt(0) || '') +
            (apiMessage.patientName?.charAt(1) || '')
        ).toUpperCase()

        return {
            id: apiMessage.id || `msg-${Date.now()}-${Math.random()}`,
            messageDirection: apiMessage.messageDirection,
            communicationMode,
            patientName: apiMessage.patientName,
            patientId: apiMessage.patientId,
            patientProfileUrl: apiMessage.patientProfileUrl,
            agentName: apiMessage.agentName,
            agentId: apiMessage.agentId,
            agentProfileUrl: apiMessage.agentProfileUrl,
            agentInitials,
            patientInitials,
            content: apiMessage.content,
            messageType: apiMessage.messageType,
            createdDateTimeUtc: apiMessage.createdDateTimeUtc,
            emailFrom: apiMessage.emailFrom,
            emailTo: apiMessage.emailTo,
            emailSubject: apiMessage.emailSubject,
            status: apiMessage.status,
            attachments: apiMessage.attachments || [],
            hasMMSAttachment: apiMessage.hasMMSAttachment || 0,
        }
    }
    clearChat() {
        this._selectedPatientSubject.next(null)
        this._selectedChatSubject.next([])
    }
    getChatAndSelectedPateint(id: string): Observable<void> {
        const currentList = this._patientsSubject.value || []
        const updatedList = currentList.map((item) => ({
            ...item,
            isSelected: item.id === id,
        }))
        let sortedList
        if (this.currentFilterStatus === 'Unread') {
            sortedList = updatedList.filter((item) => item.messageCount > 0)
        } else {
            sortedList = updatedList.sort((a, b) => {
                if (a.messageCount > 0 && b.messageCount === 0) {
                    return -1
                } else if (a.messageCount === 0 && b.messageCount > 0) {
                    return 1
                }
                return 0
            })
        }
        this.zone.run(() => {
            this._patientsSubject.next(sortedList)
        })

        if (id) {
            return this.getSelectedPatientDetails(id).pipe(
                tap((details) => {
                    if (Object.keys(details).length === 0) {
                        details = null
                    }
                    this._selectedPatientSubject.next(details)
                }),
                switchMap(() => this.loadInitialMessages(id)),
                map(() => void 0),
                finalize(() => {
                    this.zone.run(() => {
                        this._isLoadingFeedSubject.next(false)
                    })
                }),
                catchError((err) => {
                    this.selectedPatientId = null
                    return of(void 0)
                }),
            )
        }
        return of(void 0)
    }
    public followPatient(
        patientId: string,
    ): Observable<{ data: { success: boolean } }> {
        return this.http.post<{ data: { success: boolean } }>(
            `/api/v2/platform/patient/follow/${patientId}`,
            {  },
        ).pipe(
            tap((res) => {
                this.clearZeroFollowerFilter();

            })
        )
    }

    clearZeroFollowerFilter(){
        if(this.filterCriteria.enableZeroFollowerFilter){
            this.cleanUp()
           this._isLoadingFeedSubject.next(false)
            this.selectedPatientId = null
            this._clearFilterEvent.next(true)
            this.silentLoadPatientList().subscribe()
        }
    }
    public unfollowPatient(id: string): Observable<any> {
        return this.http.delete(`/api/v2/platform/patient/unfollow/${id}`).pipe(
            tap((res: any) => {
                if (
                    res &&
                    res.data &&
                    typeof res.data === 'object' &&
                    res.data['success']
                ) {
                    this.zone.run(() => {
                        const currentList = this._patientsSubject.value || []
                        const updatedList = currentList
                        .filter((item) => item.id !== id)
                        .map((item) => ({
                            ...item,
                            isSelected: false,
                        }))
                        this.loadInitialPatients().subscribe()
                        this.leaveRoomEvent.next(true);
                        this._selectedPatientSubject.next(null)
                        this._selectedChatSubject.next([])
                        this._patientsSubject.next(null)
                        this.selectedPatientId = null
                    })
                }
            }),
            catchError((err) => {
                console.error('Error unfollowing patient')
                return of(false)
            }),
        )
    }

    getPatientOpenTask(params: SearchParams) {
        const { page = 1, limit = 100 } = params
        const url = `/api/v2/platform/inbox-messaging/patient-open-tasks/${this.selectedPatientId}`
        return this.http
            .get<ApiResponse<PatientTaskOpen[]>>(url, {
                params: { page, limit },
            })
            .pipe(
                map((response) => response),
                catchError((err) => {
                    console.error('Error fetching patient tasks')
                    return of(false)
                }),
            )
    }

    markCurrentChatAsRead(patientId: string) {
        const communicationType =
            this._communicationTabSubject.getValue().toUpperCase() || 'SMS'
        const url = `/api/v2/platform/inbox-messaging/mark-as-read/${communicationType}/${patientId}`
        return this.http.put(url, {}).pipe(
            tap((response: any) => {}),
            map((response) => response),
            catchError((err) => {
                console.error('Error while marking as read')
                return of(false)
            }),
            take(1),
        )
    }

    getAgentList(params: SearchParams): Observable<any> {
        let httpParams = new HttpParams()
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                httpParams = httpParams.set(key, String(value))
            }
        })
        const url = `/api/v2/platform/inbox-messaging/filter-agent-list`
        return this.http
            .get<AgentListResponse>(url, { params: httpParams })
            .pipe(map((response: any) => response))
    }

    cleanUp() {
        this.currentFilterStatus = 'All'
        this.filterCriteria = {
            selectedUserIds: [],
            communicationType: 'All',
            dateRangePreset: null,
            customDateRange: null,
            searchTerm: null,
            isSelectAll: false,
            enableZeroFollowerFilter:false
        }
        this.enabelZeroFollowerFilter = false;
        this._selectedChatSubject.next([])
        this._selectedPatientSubject.next(null)
        this._patientsSubject.next([])
        this._communicationTabSubject.next('SMS') // Reset to default tab
    }

    cleanUpForNotifications() {
        this.cleanUp()
        this._clearFilterEvent.next(true)
        this._isLoadingFeedSubject.next(true)
        this._selectedPatientSubject.next(null)
        this._selectedChatSubject.next([])
        this._communicationTabSubject.next('SMS')
        this._allFetchedPatients = []
        this.selectedPatientId = null
    }

    clearStateForFilter() {
        this.leaveRoomEvent.next(true);
        this._selectedPatientSubject.next(null)
        this._selectedChatSubject.next([])
        this.selectedPatientId = null;

    }

    isFilterApplied(): boolean {
        const criteria = this.filterCriteria
        return (
            criteria.selectedUserIds.length > 0 ||
            criteria.isSelectAll === true ||
            criteria.communicationType !== 'All' ||
            criteria.dateRangePreset !== null ||
            !!criteria.customDateRange?.start ||
            !!criteria.customDateRange?.end ||
            criteria.searchTerm?.trim().length > 0
        )
    }

    silentLoadPatientList(): Observable<MessageItem[]> {
    this.nextCursor = null;
    this.cursorPatientId = null;
    this.cursorHasUnread = null;
    this.cursorHasContent = null;
    this.cursorPatientCreatedAt = null;
    this._totalPatientCount = 0;
    this._allFetchedPatients = [];
    this._patientsSubject.next([]);

    return this.fetchPage(
        this.morePageSize,
        this.nextCursor,
        this.cursorPatientId,
        this.direction,
    ).pipe(
        tap(({ items, totalCount, nextCursor, nextCursorPatientId, cursorHasUnread, cursorHasContent, cursorPatientCreatedAt }) => {
            this.zone.run(() => {
                this._totalPatientCount = totalCount;
                this.nextCursor = nextCursor;
                this.cursorPatientId = nextCursorPatientId;
                this.cursorHasUnread = cursorHasUnread;
                this.cursorHasContent = cursorHasContent;
                this.cursorPatientCreatedAt = cursorPatientCreatedAt;
                this._allFetchedPatients = [...items];
                if (this._allFetchedPatients.length >= totalCount) {
                    this._hasMorePagesSubject.next(false);
                } else {
                    this._hasMorePagesSubject.next(true);
                }
                this.markSelectionFlag(this._allFetchedPatients);
                let newList = this.markAsReadWhenFirstload(this._allFetchedPatients);
                this._patientsSubject.next([...newList]);
                const idExists = this._allFetchedPatients.some(
                    (item) => item.id === this.selectedPatientId,
                );
                if (idExists) {
                    this.getChatAndSelectedPateint(this.selectedPatientId).subscribe({
                        error: (err) => {
                            console.error('Error in getChatAndSelectedPateint from silentLoadPatientList', err);
                            this._isLoadingFeedSubject.next(false);
                        },
                        complete: () => {},
                    });
                }
            });
        }),
        map(({ items }) => items),
        catchError((err) => {
            console.error('Error in silentLoadPatientList', err);
            return of([]);
        }),
    );
}
}
